import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Alert from 'react-bootstrap/Alert'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Spinner from 'react-bootstrap/Spinner'
import Page from '../../../component/page'
import EditorsTable from './EditorsTable'
import EditsTable from './EditsTable'
import { isAdmin } from '../isAdmin'

// The edit monitor (/admin/edits).
//
// Public courses are editable by anyone signed in — that is what keeps the catalog correct
// when the person who added a course is long gone. The server bounds what one account can
// rewrite in a day (CourseEditGuard) and records the state every edit replaced, and this page
// is the other half: see what changed, put it back, and revoke the account's access.
//
// A bulk revert is always previewed first. The dry run and the apply are the same request with
// one flag, so what the preview lists is exactly what the apply does — and an undo of somebody
// else's work is not something to fire off unseen.
const DAY_OPTIONS = [1, 7, 30, 90, 365]

const loadEdits = (days, accountId) => ({
  type: 'ADMIN_EDITS_LOAD',
  payload: {
    request: {
      url: `/admin/edits?days=${days}${accountId ? `&accountId=${accountId}` : ''}`,
    },
  },
})

const loadEditors = (days) => ({
  type: 'ADMIN_EDITS_LOAD_EDITORS',
  payload: { request: { url: `/admin/edits/accounts?days=${days}` } },
})

const revertEdit = (auditId) => ({
  type: 'ADMIN_EDITS_REVERT',
  payload: { request: { url: `/admin/edits/${auditId}/revert`, method: 'post' } },
})

const revertAccount = (accountId, days, dryRun) => ({
  type: 'ADMIN_EDITS_REVERT',
  payload: {
    request: {
      url: '/admin/edits/revert-account',
      method: 'post',
      data: { accountId, days, dryRun },
    },
  },
})

const blockAccount = (accountId, blocked, reason) => ({
  type: 'ADMIN_EDITS_BLOCK',
  payload: {
    request: {
      url: `/admin/edits/account/${accountId}/block`,
      method: 'post',
      data: { blocked, reason },
    },
  },
})

export default function Edits() {
  const dispatch = useDispatch()
  const user = useSelector((state) => state.auth.user)
  const { edits, editors, loading, error, report, working } = useSelector((state) => state.adminEdits)
  const [days, setDays] = useState(30)
  const [accountId, setAccountId] = useState(null)
  // The account a previewed bulk revert belongs to, so the apply cannot drift onto another one.
  const [pendingRevert, setPendingRevert] = useState(null)

  const reload = React.useCallback(() => {
    dispatch(loadEdits(days, accountId)).catch(() => {})
    dispatch(loadEditors(days)).catch(() => {})
  }, [dispatch, days, accountId])

  useEffect(() => {
    reload()
  }, [reload])

  if (!isAdmin(user)) return null

  const run = (action, after) =>
    dispatch(action)
      .then(() => {
        if (after) after()
      })
      .catch(() => {})

  const onRevert = (auditId) => run(revertEdit(auditId), reload)

  const onRevertAccount = (id) => {
    setPendingRevert(id)
    run(revertAccount(id, days, true))
  }

  const applyPendingRevert = () => {
    const id = pendingRevert
    setPendingRevert(null)
    run(revertAccount(id, days, false), reload)
  }

  const onBlock = (id, blocked) => {
    const reason = blocked
      ? window.prompt('Why is this user being blocked? (shown to them when an edit is refused)') ?? ''
      : null
    if (blocked && reason === '') return
    run(blockAccount(id, blocked, reason), reload)
  }

  return (
    <Page className="admin-edits">
      <h1>Edit monitor</h1>
      <p className="text-muted">
        Every change signed-in users have made to public courses, layouts and course details.
        Private courses are their owner&apos;s alone and never appear here.
      </p>

      <Form className="d-flex align-items-end gap-3 mb-3">
        <Form.Group>
          <Form.Label className="small mb-1">Window</Form.Label>
          <Form.Select
            size="sm"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            aria-label="Window"
          >
            {DAY_OPTIONS.map((option) => (
              <option key={option} value={option}>
                Last {option} day{option === 1 ? '' : 's'}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        {accountId && (
          <Button size="sm" variant="outline-secondary" onClick={() => setAccountId(null)}>
            Showing one user — show everyone
          </Button>
        )}
        <Button size="sm" variant="outline-primary" onClick={reload} disabled={loading}>
          Refresh
        </Button>
        {(loading || working) && <Spinner animation="border" size="sm" />}
      </Form>

      {error && <Alert variant="danger">{error}</Alert>}

      {report && (
        <Alert variant={report.dryRun ? 'warning' : 'success'}>
          <strong>
            {report.dryRun ? 'Preview: would revert' : 'Reverted'} {report.reverted.length}
          </strong>
          {report.skipped.length > 0 && <> · skipped {report.skipped.length}</>}
          <ul className="small mb-2 mt-2">
            {[...report.reverted, ...report.skipped].map((outcome) => (
              <li key={`${outcome.entityType}-${outcome.entityId}-${outcome.auditId}`}>
                {outcome.entityType.toLowerCase()} {outcome.entityId}: {outcome.detail}
              </li>
            ))}
          </ul>
          {report.dryRun && pendingRevert && (
            <Button size="sm" variant="danger" onClick={applyPendingRevert} disabled={working}>
              Apply these reverts
            </Button>
          )}
        </Alert>
      )}

      <h2 className="h5 mt-4">Editors</h2>
      <EditorsTable
        editors={editors}
        working={working}
        onFilterAccount={setAccountId}
        onRevertAccount={onRevertAccount}
        onBlock={onBlock}
      />

      <h2 className="h5 mt-4">Edits</h2>
      <EditsTable
        edits={edits}
        working={working}
        onRevert={onRevert}
        onFilterAccount={setAccountId}
      />
    </Page>
  )
}
